import { ApolloClient, gql } from 'apollo-boost';

export interface NpoFinancialSummary {
  taxPrdYr: number | null;
  pdfUrl990: string | null;
}

export interface NpoProfile {
  dbId: number;
  ein: string;
  name: string | null;
  city: string | null;
  state: string | null;
  ppUpdatedAt: string | null;
  accountId: number | null;
  financialSummary: NpoFinancialSummary | null;
}

export interface NpoProfilesPage {
  totalCount: number;
  nodes: NpoProfile[];
}

export interface NpoProfilesVariables {
  page: number;
  perPage: number;
  search?: string | null;
  ein?: string | null;
}

interface NpoProfilesResponse {
  allNpoProfilesResolve: {
    totalCount: number;
    edges: { node: NpoProfile }[];
  };
}

const arrayCursor = (index: number): string =>
  window.btoa(`arrayconnection:${index}`);

const NPO_PROFILE_NODE_FIELDS = `
  dbId
  ein
  name
  city
  state
  ppUpdatedAt
  accountId
  financialSummary {
    taxPrdYr
    pdfUrl990
  }
`;

// The backend treats an explicit null filter value as a real (non-matching)
// filter, so only declare/pass `filters` fields that are actually populated.
export const getNpoProfiles = async (
  client: ApolloClient<object>,
  variables: NpoProfilesVariables,
): Promise<NpoProfilesPage> => {
  const search = variables.search?.trim() || '';
  const ein = variables.ein?.trim() || '';
  const after =
    variables.page > 0 ? arrayCursor(variables.page * variables.perPage - 1) : null;

  const varDefs = ['$first: Int', '$after: String'];
  const filterParts: string[] = [];
  const queryVariables: Record<string, unknown> = {
    first: variables.perPage,
    after,
  };
  if (search) {
    varDefs.push('$search: String');
    filterParts.push('search: $search');
    queryVariables.search = search;
  }
  if (ein) {
    varDefs.push('$ein: String');
    filterParts.push('ein: $ein');
    queryVariables.ein = ein;
  }
  const filtersArg = filterParts.length
    ? `, filters: { ${filterParts.join(', ')} }`
    : '';

  const query = gql(`
    query IrsNpoProfiles(${varDefs.join(', ')}) {
      allNpoProfilesResolve(first: $first, after: $after${filtersArg}) {
        totalCount
        edges {
          node {${NPO_PROFILE_NODE_FIELDS}}
        }
      }
    }
  `);

  const res = await client.query<NpoProfilesResponse>({
    query,
    variables: queryVariables,
    fetchPolicy: 'network-only',
  });
  const conn = res.data.allNpoProfilesResolve;
  return {
    totalCount: conn.totalCount,
    nodes: conn.edges.map(e => e.node),
  };
};

export interface NpoSyncLog {
  dbId: number;
  syncType: string;
  status: string;
  inProgress: boolean;
  itemsTotal: number;
  itemsProcessed: number;
  itemsSkipped: number;
  itemsFailed: number;
  itemsRemaining: number;
  startedAt: string | null;
  endedAt: string | null;
  triggerEin: string | null;
  error: string | null;
}

interface NpoSyncLogsResponse {
  allNpoSyncLogs: {
    edges: { node: NpoSyncLog }[];
  };
}

const npoSyncLogsQuery = gql`
  query IrsNpoSyncLogs($last: Int) {
    allNpoSyncLogs(last: $last) {
      edges {
        node {
          dbId
          syncType
          status
          inProgress
          itemsTotal
          itemsProcessed
          itemsSkipped
          itemsFailed
          itemsRemaining
          startedAt
          endedAt
          triggerEin
          error
        }
      }
    }
  }
`;

export const getSyncLogs = async (
  client: ApolloClient<object>,
  variables: { last: number },
): Promise<NpoSyncLog[]> => {
  const res = await client.query<NpoSyncLogsResponse>({
    query: npoSyncLogsQuery,
    variables,
    fetchPolicy: 'network-only',
  });
  return res.data.allNpoSyncLogs.edges.map(e => e.node);
};

export interface TriggerNpoSyncResult {
  success: boolean;
  logId: number;
  status: string;
}

interface TriggerNpoSyncResponse {
  triggerNpoSync: TriggerNpoSyncResult;
}

const triggerNpoSyncMutation = gql`
  mutation TriggerNpoSync($syncType: String!, $ein: String) {
    triggerNpoSync(syncType: $syncType, ein: $ein) {
      success
      logId
      status
    }
  }
`;

export const triggerNpoSync = async (
  client: ApolloClient<object>,
  variables: { syncType: 'all' | 'single_ein'; ein?: string | null },
): Promise<TriggerNpoSyncResult | undefined> => {
  const res = await client.mutate<TriggerNpoSyncResponse>({
    mutation: triggerNpoSyncMutation,
    variables,
  });
  return res.data?.triggerNpoSync;
};

export interface CancelNpoSyncResult {
  success: boolean;
  status: string;
}

interface CancelNpoSyncResponse {
  cancelNpoSync: CancelNpoSyncResult;
}

const cancelNpoSyncMutation = gql`
  mutation CancelNpoSync($logId: Int!) {
    cancelNpoSync(logId: $logId) {
      success
      status
    }
  }
`;

export const cancelNpoSync = async (
  client: ApolloClient<object>,
  variables: { logId: number },
): Promise<CancelNpoSyncResult | undefined> => {
  const res = await client.mutate<CancelNpoSyncResponse>({
    mutation: cancelNpoSyncMutation,
    variables,
  });
  return res.data?.cancelNpoSync;
};
